import React, { useState, useEffect } from 'react';
import axios from 'axios';

function Dashboard({ stats, onNavigate, host, onHostChange }) {
  const [editingHost, setEditingHost] = useState(false);
  const [hostValue, setHostValue] = useState(host || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setHostValue(host || '');
  }, [host]);

  const handleSaveHost = async () => {
    setSaving(true);
    try {
      await axios.put('/api/config', { host: hostValue });
      onHostChange(hostValue);
      setEditingHost(false);
    } catch (error) {
      console.error('Error saving host:', error);
    }
    setSaving(false);
  };


  return (
    <div>
      <h1>Dashboard</h1>
      <p>Welcome to Spider Web Automation - A CSV-Driven API Testing Platform</p>

      {/* HOST Configuration */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <h3 style={{ margin: '0 0 10px 0' }}>HOST Configuration</h3>
        {editingHost ? (
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="text"
              value={hostValue}
              onChange={(e) => setHostValue(e.target.value)}
              placeholder="e.g., https://abcd.xyz.com/api"
              style={{ flex: 1, minWidth: '300px', padding: '8px' }}
            />
            <button
              className="btn btn-success"
              onClick={handleSaveHost}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setHostValue(host || '');
                setEditingHost(false);
              }}
            >
              Cancel
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            <code
              style={{
                flex: 1,
                padding: '8px 12px',
                backgroundColor: 'var(--bg-input)',
                border: '1px solid var(--border)',
                borderRadius: '4px',
                fontSize: '14px',
                color: host ? 'var(--text-main)' : 'var(--text-muted)'
              }}
            >
              {host || 'Not configured - Click Edit to set HOST'}
            </code>
            <button className="btn btn-primary" onClick={() => setEditingHost(true)}>
              Edit
            </button>
          </div>
        )}
        <small style={{ display: 'block', marginTop: '8px', color: 'var(--text-muted)' }}>
          Set the base URL here. In API Collection, relative paths are auto-prefixed with this HOST.
        </small>
      </div>

      <div className="grid">
        <div className="stat-card">
          <h3>{stats.apis}</h3>
          <p>API Tests Created</p>
        </div>
        <div className="stat-card">
          <h3>{stats.csvs}</h3>
          <p>CSV Files Uploaded</p>
        </div>
        <div className="stat-card">
          <h3>{stats.suites}</h3>
          <p>Test Suites Created</p>
        </div>
      </div>

      <div className="card">
        <h2 style={{ marginBottom: '20px' }}>Quick Start Guide</h2>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '14px', marginBottom: '28px' }}>
          {[
            {
              step: '1',
              color: '#3b82f6',
              title: 'Set HOST',
              desc: 'Configure your base API URL using the HOST Configuration panel above. Relative paths like /v1/users are auto-prefixed.',
            },
            {
              step: '2',
              color: '#10b981',
              title: 'Add APIs to Collection',
              desc: 'Go to API Collection → create entries with HTTP method, path, headers, and an optional JSON payload template.',
            },
            {
              step: '3',
              color: '#f59e0b',
              title: 'Upload CSV Data',
              desc: 'Go to Data Sets → upload a CSV. Each row becomes one test scenario. Column headers become {{variable}} names.',
            },
            {
              step: '4',
              color: '#8b5cf6',
              title: 'Configure Test Steps',
              desc: 'Go to Test Steps → map CSV columns to API fields. Add Capture Rules to extract response values (e.g. $.data.id) into variables for later steps.',
            },
            {
              step: '5',
              color: '#ec4899',
              title: 'Build & Run Test Suites',
              desc: 'Go to Test Suite Builder → chain steps into sequential workflows (Login → Create → Verify). Hit Execute to run at scale.',
            },
            {
              step: '6',
              color: '#06b6d4',
              title: 'Review Results',
              desc: 'Go to Execution Results → inspect pass/fail per row, view response bodies, download detailed PDF/CSV reports.',
            },
          ].map(({ step, color, title, desc }) => (
            <div key={step} style={{
              display: 'flex',
              gap: '14px',
              alignItems: 'flex-start',
              background: 'var(--bg-input)',
              border: `1px solid var(--border)`,
              borderLeft: `4px solid ${color}`,
              borderRadius: '8px',
              padding: '14px',
            }}>
              <div style={{
                minWidth: '28px',
                height: '28px',
                borderRadius: '50%',
                backgroundColor: color,
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                fontSize: '13px',
              }}>{step}</div>
              <div>
                <strong style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>{title}</strong>
                <span style={{ fontSize: '13px', color: 'var(--text-muted)', lineHeight: '1.5' }}>{desc}</span>
              </div>
            </div>
          ))}
        </div>

        <h3 style={{ marginBottom: '12px' }}>Key Features</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '8px' }}>
          {[
            '🔗 Configurable HOST per environment',
            '📄 CSV-driven test data (unlimited rows)',
            '🔀 {{variable}} injection in URL, headers & body',
            '📥 Response Capture Rules for data chaining',
            '🔁 Sequential multi-step orchestration',
            '⚡ Custom JS Functions (HMAC, masks, etc.)',
            '🧩 Default Extensions (UUID, dates, random data)',
            '📊 Detailed pass/fail reports with export',
            '🔒 Multi-user support with session management',
            '🌐 Supports GET, POST, PUT, PATCH, DELETE',
          ].map((f) => (
            <div key={f} style={{
              fontSize: '13px',
              padding: '8px 12px',
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
              borderRadius: '6px',
              color: 'var(--text-main)',
            }}>{f}</div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
